import type { SanityCallToAction } from '#/lib/sanity/queries/sections';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';

import { Button } from './Button';
import { ButtonRow } from './ButtonRow';
import { FormCustom } from './Form/Custom';
import { HomepageEmailForm } from './Form/HomepageEmail';
import { getHeadlineHighlightClass, Headline } from './Headline';
import { Media } from './Media';
import { SvgIcon } from './SvgIcon';

export function CallToAction(
  props: SanityCallToAction & PageLayoutSharedProps,
) {
  const hasLegacyColors = Boolean(props.background || props.foreground);
  const legacyHighlightClass = getHeadlineHighlightClass({
    background: props.background,
    foreground: props.foreground,
  });
  const highlightClass = hasLegacyColors
    ? legacyHighlightClass
    : getSectionThemeAccent(props.theme);
  const buttonTheme = hasLegacyColors
    ? legacyHighlightClass
      ? 'dark'
      : 'light'
    : getSectionButtonTheme(props.theme);
  // Charcoal gets dark form controls; purple/light stay light (same as HeroSplit).
  const formTheme =
    !hasLegacyColors && props.theme === 'charcoal' ? 'dark' : 'light';
  const ctaMode = props.ctaMode ?? 'button';
  const isDarkSurface = hasLegacyColors
    ? Boolean(legacyHighlightClass)
    : isDarkSectionTheme(props.theme);

  return (
    <div
      className={cn(
        'p-main pb-100 pt-65 text-center',
        getSectionThemeSurface(props.theme),
      )}
      style={{
        background: props.background ?? undefined,
        color: props.foreground ?? undefined,
      }}
    >
      <div className="fade-in mx-auto flex max-w-[820px] flex-col items-center">
        {props.media?._type === 'icon' ? (
          <SvgIcon
            svg={props.media.svg}
            className="mx-auto mb-25 text-current [&_svg]:size-80"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'media',
            )}
          />
        ) : props.media ? (
          <Media
            className="mx-auto mb-25 w-118"
            media={props.media}
            sizes="118px"
          />
        ) : null}
        <Headline
          value={props.headline}
          variant={3}
          highlightClass={highlightClass}
          className="mb-12 m:mb-20"
          dataSanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'headline',
          )}
        />
        {props.bodyCopy ? (
          <p
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'bodyCopy',
            )}
            className={cn(
              isDarkSurface ? 'text-body-cloud' : 'text-body',
              'mx-auto max-w-[820px] whitespace-pre-wrap text-pretty',
            )}
            style={{ color: props.foreground ?? undefined }}
          >
            {props.bodyCopy}
          </p>
        ) : null}

        {ctaMode === 'button' && props.cta?.href ? (
          <Button
            href={props.cta.href}
            target={props.cta.target}
            className="mt-25 m:mt-30"
            variant="primary"
            theme={buttonTheme}
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'cta',
            )}
          >
            {props.cta.title}
          </Button>
        ) : null}

        {ctaMode === 'buttonRow' && props.buttons?.length ? (
          <div
            className="@container mt-25 flex w-full justify-center m:mt-30"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'buttons',
            )}
          >
            <ButtonRow buttons={props.buttons} theme={buttonTheme} />
          </div>
        ) : null}

        {ctaMode === 'standardForm' && props.form ? (
          <div className="mt-40 flex w-full flex-col items-center gap-16 m:flex-row m:items-start m:justify-center m:gap-24">
            <div
              className="w-full max-w-[500px] text-left"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'form',
              )}
            >
              <FormCustom
                className={cn(
                  isDarkSurface &&
                    '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
                )}
                form={props.form}
                theme={formTheme}
                buttonTheme={buttonTheme}
                confirmation={props.formConfirmation ?? ''}
              />
            </div>
            {props.secondaryButton?.href ? (
              <Button
                variant="secondary"
                theme={formTheme}
                href={props.secondaryButton.href}
                target={props.secondaryButton.target}
                className="hidden h-50 items-center justify-center self-start text-center m:flex"
                data-sanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'secondaryButton',
                )}
              >
                {props.secondaryButton.title}
              </Button>
            ) : null}
          </div>
        ) : null}

        {ctaMode === 'redirectForm' &&
        (props.formFunnel === 'rails' || props.form?.formType === 'contact') ? (
          <div className="mt-40 flex w-full flex-col items-center gap-16 m:flex-row m:items-start m:justify-center m:gap-24">
            <HomepageEmailForm
              className="w-full max-w-[416px]"
              form={props.form?.formType === 'contact' ? props.form : null}
              formFunnel={props.formFunnel ?? 'contact'}
              primaryButtonLabel={props.primaryButtonLabel ?? 'Get a demo'}
              theme={formTheme}
              legalText={props.legalText}
              legalTextClassName={cn(
                'text-left',
                // Purple keeps light inputs; privacy copy still needs to
                // read on the cipher surface (same as FormCustom above).
                isDarkSurface && '!text-cloud',
              )}
            />
            {props.secondaryButton?.href ? (
              <Button
                variant="secondary"
                theme={formTheme}
                href={props.secondaryButton.href}
                target={props.secondaryButton.target}
                className="hidden h-50 items-center justify-center self-start text-center m:flex"
                data-sanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  'secondaryButton',
                )}
              >
                {props.secondaryButton.title}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
